import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress, Progress } from "@nextui-org/react";
import { RecordIcon } from "../../icons/RecordIcon";
import { IS_IOS } from "../../utils";
import { trackEvent } from "../../../utils/analytics";

const RealtimeCamera = ({ onCapture, uploading, debug = false, flowId, verificationId }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const playbackVideoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const faceLandmarkerRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Steps: center → left → center → right
  const steps = [
    t("liveness.realtimeCamera.steps.center"),
    t("liveness.realtimeCamera.steps.left"),
    t("liveness.realtimeCamera.steps.returnCenter"),
    t("liveness.realtimeCamera.steps.right"),
  ];

  const [step, setStep] = useState(0);
  const [turnedLeft, setTurnedLeft] = useState(false);
  const [turnedRight, setTurnedRight] = useState(false);
  const [holdStart, setHoldStart] = useState(null);
  const [progress, setProgress] = React.useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [recordedBlobs, setRecordedBlobs] = useState([]);
  const [faceLandmarkerReady, setFaceLandmarkerReady] = useState(false);
  const [showPlayback, setShowPlayback] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState(null);

  const stepRef = useRef(0);
  const holdStartRef = useRef(null);
  const isRecordingRef = useRef(false);
  const progressIntervalRef = useRef(null);
  const lastVideoTimeRef = useRef(-1);

  const totalTime = 10000; // total time in milliseconds to reach 100%
  const updateInterval = 100; // update every 100 milliseconds
  const incrementValue = 100 / (totalTime / updateInterval); // calculate increment per update

  const clear = () => {
    setStep(0);
    setTurnedLeft(false);
    setTurnedRight(false);
    setHoldStart(null);
  };

  const stopCamera = () => {
    // Read the live stream off the video element, not a piece of React
    // state — callers reached through a stale closure (e.g. the mount
    // effect's unmount cleanup below) always see the current DOM value.
    const activeStream = videoRef.current?.srcObject;
    if (activeStream) {
      activeStream.getTracks().forEach((track) => track.stop());
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  };

  const startCamera = async () => {
    // Guard on the live DOM stream so this is safe to call right after
    // stopCamera() in the same tick.
    if (videoRef.current?.srcObject) return;

    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480 },
      });

      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
        };
      }
    } catch (error) {
      console.error("Error starting camera:", error);
    }
  };

  // The camera can be interrupted by backgrounding the app (lock screen, app
  // switch): a dead/muted track leaves a frozen feed, and on iOS (Safari and
  // every other iOS browser, since they all run on WebKit) the video element
  // can paint a stale/blank frame on resume even while the track itself still
  // reports readyState "live" and muted: false. Mirrors the fix in
  // DocumentCamera.jsx.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      const track = videoRef.current?.srcObject?.getVideoTracks?.()?.[0];
      const trackUnhealthy = track && (track.readyState !== "live" || track.muted);
      if (trackUnhealthy || IS_IOS) {
        stopCamera();
        startCamera();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showRecordedVideo = (videoUrl) => {
    setShowPlayback(true);
    setCurrentVideoUrl(videoUrl);
  };

  const handleProceed = React.useCallback(
    (blob) => {
      onCapture({
        type: "video",
        value: blob,
      });
    },
    [onCapture]
  );

  // Initialize MediaPipe FaceLandmarker
  useEffect(() => {
    const initializeFaceLandmarker = async () => {
      try {
        // Wait for MediaPipe to be available
        const waitForMediaPipe = () => {
          return new Promise((resolve) => {
            const checkMediaPipe = () => {
              if (window.MediaPipeVision) {
                resolve();
              } else {
                setTimeout(checkMediaPipe, 100);
              }
            };
            checkMediaPipe();
          });
        };

        await waitForMediaPipe();

        const { FaceLandmarker, FilesetResolver } = window.MediaPipeVision;

        const filesetResolver = await FilesetResolver.forVisionTasks(
          "/mediapipe/wasm"
        );

        const faceLandmarker = await FaceLandmarker.createFromOptions(
          filesetResolver,
          {
            baseOptions: {
              modelAssetPath: "/mediapipe/models/face_landmarker.task",
              delegate: "GPU",
            },
            outputFaceBlendshapes: false,
            runningMode: "VIDEO",
            numFaces: 1,
          }
        );

        faceLandmarkerRef.current = faceLandmarker;
        setFaceLandmarkerReady(true);
        console.log("FaceLandmarker initialized successfully");
      } catch (error) {
        console.error("Error initializing FaceLandmarker:", error);
      }
    };

    initializeFaceLandmarker();

    return () => {
      if (faceLandmarkerRef.current) {
        faceLandmarkerRef.current.close();
      }
    };
  }, []);

  const calculateHeadPose = (landmarks) => {
    if (!landmarks || landmarks.length === 0)
      return { yaw: 0, centered: false };

    // Use key facial landmarks for head pose estimation
    const noseTip = landmarks[1]; // Nose tip
    const leftEyeCorner = landmarks[33]; // Left eye corner
    const rightEyeCorner = landmarks[263]; // Right eye corner

    // Calculate yaw based on eye distance ratio and nose position
    const eyeDistance = Math.abs(rightEyeCorner.x - leftEyeCorner.x);
    const leftEyeToNose = Math.abs(noseTip.x - leftEyeCorner.x);
    const rightEyeToNose = Math.abs(noseTip.x - rightEyeCorner.x);

    // Normalize the yaw calculation
    const yawRatio = (leftEyeToNose - rightEyeToNose) / eyeDistance;
    const yaw = yawRatio * 100; // Scale for easier threshold comparison

    // Check if face is centered (yaw close to 0)
    const centered = Math.abs(yaw) < 15;

    return { yaw, centered };
  };

  const updateStep = (yaw, centered, isCurrentlyRecording) => {
    // Only allow step progression when recording
    if (!isCurrentlyRecording) return;

    const currentStep = stepRef.current;
    const currentHoldStart = holdStartRef.current;

    switch (currentStep) {
      case 0: // center first
        if (centered) setStep(1);
        break;
      case 1: // look left
        if (yaw > 20) {
          if (!currentHoldStart) {
            setHoldStart(Date.now());
          } else if (Date.now() - currentHoldStart > 800) {
            setStep(2);
            setTurnedLeft(true);
            setHoldStart(null);
          }
        } else {
          setHoldStart(null);
        }
        break;
      case 2: // return to center
        if (centered) setStep(3);
        break;
      case 3: // look right
        if (yaw < -20) {
          if (!currentHoldStart) {
            setHoldStart(Date.now());
          } else if (Date.now() - currentHoldStart > 800) {
            setStep(4);
            setHoldStart(null);
            setTurnedRight(true);
            // Liveness check passed - stop recording
            setTimeout(() => {
              setIsCapturing(false);
              setProgress(0);
              stopRecording();
              if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
                progressIntervalRef.current = null;
              }
            }, 500); // Small delay to show completion
          }
        } else {
          setHoldStart(null);
        }
        break;
      case 4:
        // ✅ done – no more checks
        break;
      default:
        break;
    }
  };

  const processVideoFrame = async () => {
    const video = videoRef.current;
    const faceLandmarker = faceLandmarkerRef.current;

    if (!video || !faceLandmarker || !faceLandmarkerReady) {
      animationFrameRef.current = requestAnimationFrame(processVideoFrame);
      return;
    }

    // Only process if video time has changed
    if (video.currentTime !== lastVideoTimeRef.current) {
      lastVideoTimeRef.current = video.currentTime;

      try {
        const startTimeMs = performance.now();
        const results = faceLandmarker.detectForVideo(video, startTimeMs);

        if (results.faceLandmarks && results.faceLandmarks.length > 0) {
          const landmarks = results.faceLandmarks[0];
          const { yaw, centered } = calculateHeadPose(landmarks);

          // Update step progression
          updateStep(yaw, centered, isRecordingRef.current);
        }
      } catch (error) {
        console.error("Error processing video frame:", error);
      }
    }

    if (isRecordingRef.current) {
      animationFrameRef.current = requestAnimationFrame(processVideoFrame);
    }
  };

  const startRecording = () => {
    if (!videoRef.current || !videoRef.current.srcObject) {
      console.error("No video stream available");
      return;
    }

    try {
      const stream = videoRef.current.srcObject;
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "video/mp4",
      });

      recordedChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(recordedChunksRef.current, {
          type: "video/mp4",
        });

        // Add to recorded blobs array
        const recordedVideo = {
          blob,
          timestamp: new Date().toISOString(),
          url: URL.createObjectURL(blob),
        };

        setRecordedBlobs((prev) => [...prev, recordedVideo]);
        console.log("Recording stopped, blob created:", blob);

        // Auto-upload if liveness check was completed (step 4)
        if (stepRef.current === 4) {
          try {
            // Stop camera and show recorded video
            stopCamera();
            showRecordedVideo(recordedVideo.url);
            handleProceed(blob);
          } catch (error) {
            console.error("Auto-upload failed:", error);
            // Upload failed but recording was successful
          }
        }
      };

      mediaRecorder.start(100); // Collect data every 100ms
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      isRecordingRef.current = true;

      // Start processing video frames
      animationFrameRef.current = requestAnimationFrame(processVideoFrame);
    } catch (error) {
      console.error("Error starting recording:", error);
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      isRecordingRef.current = false;
      console.log("Recording stopped");
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  const handleStartCapture = () => {
    if (isCapturing) {
      return;
    }

    trackEvent("liveness_recording_started", { flowId, verificationId });

    // Start new capture
    setIsCapturing(true);
    setProgress(0);
    clear();
    startRecording();

    // Start progress timer
    progressIntervalRef.current = setInterval(() => {
      setProgress((currentProgress) => {
        const newProgress = currentProgress + incrementValue;
        if (newProgress >= 100) {
          // Auto-stop when progress reaches 100%
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
          setIsCapturing(false);
          stopRecording();
          clear();
          return 0;
        }
        return newProgress;
      });
    }, updateInterval);
  };

  // Keep refs in sync with state
  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  useEffect(() => {
    holdStartRef.current = holdStart;
  }, [holdStart]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  // Start camera when component mounts and FaceLandmarker is ready
  useEffect(() => {
    if (faceLandmarkerReady && !showPlayback) {
      startCamera();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faceLandmarkerReady, showPlayback]);

  useEffect(() => {
    console.log("RealtimeCamera mounted");
    return () => {
      console.log("RealtimeCamera unmounted");
      // Cleanup on unmount
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      stopRecording();
      // Clean up object URLs
      recordedBlobs.forEach((record) => {
        URL.revokeObjectURL(record.url);
      });
      if (currentVideoUrl) {
        URL.revokeObjectURL(currentVideoUrl);
      }

      // Stop camera stream
      stopCamera();

      // Close FaceLandmarker
      if (faceLandmarkerRef.current) {
        faceLandmarkerRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showPlayback && currentVideoUrl && playbackVideoRef.current) {
      const videoElement = playbackVideoRef.current;
      videoElement.src = currentVideoUrl;

      // Browser autoplay policies can sometimes block .play(). This handles potential errors.
      const playPromise = videoElement.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.error("Error attempting to play video automatically:", error);
        });
      }
    }
  }, [showPlayback, currentVideoUrl]); // This runs when the playback state is ready

  const instructionText = !faceLandmarkerReady
    ? t("liveness.realtimeCamera.loadingFaceDetection")
    : showPlayback
    ? t("liveness.realtimeCamera.recordingComplete")
    : !isRecording
    ? t("liveness.realtimeCamera.tapToRecord")
    : step < steps.length
    ? steps[step]
    : t("liveness.realtimeCamera.livenessPassed");

  const leftColor  = turnedLeft  ? "#08e899" : "#e5e7eb";
  const rightColor = turnedRight ? "#08e899" : "#e5e7eb";

  return (
    <div className="flex flex-col h-full">

      {/* Circle + instructions */}
      <div className="flex-1 flex flex-col items-center justify-center gap-6 px-6">

        {/* Circle with split left/right turn indicators */}
        <div className="relative" style={{ width: "80%", aspectRatio: "1 / 1" }}>
          <div
            className="absolute inset-0 rounded-full transition-colors duration-500"
            style={{
              background: `linear-gradient(90deg, ${leftColor} 50%, ${rightColor} 50%)`,
              padding: 6,
            }}
          >
            <div className="w-full h-full rounded-full overflow-hidden bg-white">
              {!showPlayback ? (
                <video
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  autoPlay muted playsInline
                  style={{ transform: "scaleX(-1)" }}
                />
              ) : (
                <video
                  ref={playbackVideoRef}
                  className="w-full h-full object-cover"
                  autoPlay muted playsInline loop
                  style={{ transform: "scaleX(-1)" }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="text-center flex flex-col items-center gap-2">
          <div className="heading text-lg">{t("liveness.keepFaceInOval")}</div>
          <div className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
            isRecording
              ? "bg-blue-500 text-white shadow-md"
              : "bg-gray-100 text-gray-500"
          }`}>
            {instructionText}
          </div>
        </div>

      </div>

      {/* Record button pinned to bottom */}
      <div className="flex justify-center items-center pb-10">
        {uploading ? (
          <div className="flex flex-col items-center gap-2 w-full px-8">
            <Progress size="sm" isIndeterminate aria-label="Uploading…" className="max-w-md" />
            <span className="desc text-sm">{t("liveness.realtimeCamera.uploading")}</span>
          </div>
        ) : (
          <CircularProgress
            aria-label="Recording progress"
            size="lg"
            value={progress}
            valueLabel={
              <div
                onClick={faceLandmarkerReady ? handleStartCapture : undefined}
                className={`cursor-pointer ${!faceLandmarkerReady ? "opacity-40" : ""}`}
              >
                <RecordIcon height={20} width={20} />
              </div>
            }
            color={isCapturing ? "danger" : "primary"}
            showValueLabel
          />
        )}
      </div>

      {/* Debug overlay */}
      {recordedBlobs.length > 0 && debug && (
        <div className="absolute top-4 right-4 bg-black/75 text-white p-2 rounded text-xs">
          <div>{t("liveness.realtimeCamera.debug.recordedVideos", { count: recordedBlobs.length })}</div>
          <div>FaceLandmarker: {faceLandmarkerReady ? t("liveness.realtimeCamera.debug.faceLandmarkerReady") : t("liveness.realtimeCamera.debug.faceLandmarkerLoading")}</div>
          <div>Playback: {showPlayback ? t("liveness.realtimeCamera.debug.playbackActive") : t("liveness.realtimeCamera.debug.playbackInactive")}</div>
        </div>
      )}
    </div>
  );
};

export default RealtimeCamera;
